import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, House, MapPin, CaretRight, CheckCircle, Warning, XCircle, PaperPlaneTilt, Plus, Eye, CurrencyGbp, TrendUp, ChartLine, Buildings, Thermometer, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

// Cool, modern property palette — clearly NOT cream/beige
const PAGE = "#F2F4F7";
const CARD = "#FFFFFF";
const INK = "#0B1220";
const INK_SOFT = "#4A5566";
const NAVY = "#0F4A6B";
const NAVY_DEEP = "#0A3451";
const AMBER = "#D97706";          // offers
const EMERALD = "#10B981";        // hot
const CORAL = "#EF4444";          // price-sensitive / cooling
const RULE = "#D9DEE5";

// Real property photos via Unsplash (compressed, ~400px wide)
const U = (id) => `https://images.unsplash.com/photo-${id}?w=400&h=300&auto=format&fit=crop`;

const PROPERTIES_SEED = [
  {
    id: "pr1",
    address: "12 Lakeside Walk",
    city: "Holborough",
    price: 485000,
    type: "3-bed · Semi-detached",
    img: U("1568605114967-8130f3a36994"),
    listedDays: 16,
    viewings: [
      { id: "v1", buyer: "Mr & Mrs Patel", when: "Sat 10:00", outcome: "yes", offer: 470000, note: "Loved the garden. Willing to go to £475 if survey is clean." },
      { id: "v2", buyer: "Ms Green", when: "Sat 11:30", outcome: "maybe", offer: null, note: "Kitchen too small; asked about extension possibilities." },
      { id: "v3", buyer: "Mr Clarke", when: "Sun 14:00", outcome: "price", offer: 455000, note: "Thinks asking is £15k over." },
    ],
  },
  {
    id: "pr2",
    address: "Oak Cottage, Willow Lane",
    city: "Holborough",
    price: 625000,
    type: "4-bed · Detached",
    img: U("1449844908441-8829872d2607"),
    listedDays: 42,
    viewings: [
      { id: "v4", buyer: "The Jacksons", when: "Fri 18:00", outcome: "no", offer: null, note: "Decided against — needs work they don't want." },
    ],
  },
  {
    id: "pr3",
    address: "Flat 7, The Boathouse",
    city: "Holborough Quay",
    price: 310000,
    type: "2-bed · Apartment",
    img: U("1522708323590-d24dbb6b0267"),
    listedDays: 3,
    viewings: [],
  },
];

const OUTCOMES = [
  { key: "yes", label: "Offered", color: EMERALD, icon: CheckCircle },
  { key: "maybe", label: "Maybe", color: AMBER, icon: Warning },
  { key: "price", label: "Price-sensitive", color: CORAL, icon: Warning },
  { key: "no", label: "No", color: INK_SOFT, icon: XCircle },
];

/**
 * Temperature signal derived from viewings — what the agent sees at a glance
 * and what the vendor reads in the weekly summary.
 */
function getSignal(property) {
  const vs = property.viewings;
  if (vs.length === 0) {
    return property.listedDays > 7
      ? { key: "quiet", label: "Quiet", note: "No viewings this week · promote", color: INK_SOFT }
      : { key: "new", label: "Just listed", note: "Too early to read · 3d old", color: NAVY };
  }
  const yes = vs.filter((v) => v.outcome === "yes");
  const price = vs.filter((v) => v.outcome === "price");
  const maybe = vs.filter((v) => v.outcome === "maybe");
  if (yes.length >= 1) return { key: "hot", label: "Hot", note: `${yes.length} offer${yes.length === 1 ? "" : "s"} · move to decision`, color: EMERALD };
  if (price.length >= 2) return { key: "price", label: "Price-sensitive", note: `${price.length} buyers flagged asking · consider reduction`, color: CORAL };
  if (maybe.length >= 2) return { key: "warm", label: "Warm", note: `${maybe.length} on the fence · small tweak could close`, color: AMBER };
  return { key: "cool", label: "Cooling", note: "Interest dropping · review listing", color: INK_SOFT };
}

export default function OpenHouseTracker() {
  const [properties, setProperties] = useState(PROPERTIES_SEED);
  const [selectedId, setSelectedId] = useState(null);      // null = list view
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState({ buyer: "", when: "", outcome: "maybe", offer: "", note: "" });

  const selected = selectedId ? properties.find((p) => p.id === selectedId) : null;
  const navigate = useNavigate();

  const overview = useMemo(() => {
    const totalViewings = properties.reduce((t, p) => t + p.viewings.length, 0);
    const totalOffers = properties.reduce((t, p) => t + p.viewings.filter((v) => v.outcome === "yes").length, 0);
    const priceFlags = properties.reduce((t, p) => t + p.viewings.filter((v) => v.outcome === "price").length, 0);
    const topOffer = Math.max(0, ...properties.flatMap((p) => p.viewings.filter((v) => v.outcome === "yes").map((v) => v.offer || 0)));
    return { totalViewings, totalOffers, priceFlags, topOffer };
  }, [properties]);

  const addViewing = () => {
    if (!form.buyer || !form.when) { toast.error("Buyer name and time required"); return; }
    const viewing = { id: "v" + Date.now(), buyer: form.buyer, when: form.when, outcome: form.outcome, offer: form.offer ? Number(form.offer) : null, note: form.note };
    setProperties((ps) => ps.map((p) => p.id === selectedId ? { ...p, viewings: [viewing, ...p.viewings] } : p));
    toast.success("Viewing logged · vendor will see it in Friday's summary");
    setLogOpen(false);
    setForm({ buyer: "", when: "", outcome: "maybe", offer: "", note: "" });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="openhouse-interactive">
      <header className="sticky top-0 z-20 border-b" style={{ backgroundColor: CARD, borderColor: RULE }}>
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {selected ? (
              <button onClick={() => setSelectedId(null)} className="opacity-70 hover:opacity-100" data-testid="openhouse-back"><ArrowLeft size={18} /></button>
            ) : (
              <Link to="/demos/open-house-tracker" className="opacity-60 hover:opacity-100"><ArrowLeft size={18} /></Link>
            )}
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-9 w-9 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: INK }}>
                <Buildings size={16} weight="fill" color={AMBER} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight truncate" style={{ fontFamily: "'Outfit', serif" }}>Rooksbury &amp; Co.</div>
                <div className="text-[9px] font-mono uppercase tracking-widest opacity-60">Open-house tracker · concept mock</div>
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=open-house-tracker")} className="text-xs font-semibold px-3 py-1.5 border hidden sm:block" style={{ borderColor: INK, color: INK }}>
            Book a call
          </button>
        </div>
      </header>

      {!selected && <ListView properties={properties} overview={overview} onSelect={setSelectedId} />}
      {selected && <DetailView property={selected} onLogOpen={() => setLogOpen(true)} />}

      <footer className="border-t mt-8 py-8" style={{ backgroundColor: CARD, borderColor: RULE }}>
        <div className="max-w-3xl mx-auto px-5 text-center">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-3">Concept · a little dashboard for your Thursday</p>
          <Link to="/consultation?demo=open-house-tracker" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: NAVY }}>Talk to us about a build like this <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>

      {/* Floating log button on detail view */}
      {selected && (
        <button onClick={() => setLogOpen(true)} className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white" style={{ backgroundColor: NAVY }} data-testid="openhouse-fab-log" aria-label="Log a viewing">
          <Plus size={22} weight="bold" />
        </button>
      )}

      {logOpen && selected && (
        <LogModal property={selected} form={form} setForm={setForm} onCancel={() => setLogOpen(false)} onSave={addViewing} />
      )}
    </div>
  );
}

function ListView({ properties, overview, onSelect }) {
  return (
    <section className="max-w-3xl mx-auto px-5 py-6">
      <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">This week · Mon 21 – Sun 27</p>
      <h1 className="text-4xl font-black tracking-tighter mb-5 leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>Your listings</h1>

      {/* Overview strip */}
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="openhouse-overview">
        <StatMini icon={Eye} label="Viewings" value={overview.totalViewings} accent={NAVY} />
        <StatMini icon={TrendUp} label="Offers" value={overview.totalOffers} accent={EMERALD} />
        <StatMini icon={Thermometer} label="Price flags" value={overview.priceFlags} accent={overview.priceFlags > 0 ? CORAL : INK_SOFT} />
      </div>

      {/* Top offer ribbon */}
      {overview.topOffer > 0 && (
        <div className="mb-5 px-4 py-3 flex items-center gap-3" style={{ backgroundColor: "#ECFDF5", border: `1px solid ${EMERALD}33` }}>
          <CheckCircle size={18} weight="fill" style={{ color: EMERALD }} />
          <div className="flex-1 text-sm"><b>Top offer this week:</b> <b style={{ color: EMERALD }}>£{overview.topOffer.toLocaleString()}</b> · 12 Lakeside Walk · move to decision with the vendor</div>
        </div>
      )}

      {/* Compact listing cards — mobile-first horizontal layout */}
      <div className="space-y-3" data-testid="openhouse-list">
        {properties.map((p) => {
          const sig = getSignal(p);
          const topOffer = p.viewings.filter((v) => v.outcome === "yes" && v.offer).sort((a, b) => b.offer - a.offer)[0];
          return (
            <button key={p.id} onClick={() => onSelect(p.id)} className="group w-full bg-white border transition-all hover:shadow-[3px_3px_0_0_rgba(11,18,32,0.08)] hover:border-[color:var(--h)]" style={{ borderColor: RULE, "--h": NAVY }} data-testid={`openhouse-card-${p.id}`}>
              <div className="flex items-stretch gap-0">
                {/* thumbnail — compact on mobile */}
                <div className="relative w-28 sm:w-36 flex-shrink-0 aspect-[4/3] sm:aspect-auto bg-slate-100 overflow-hidden">
                  <img src={p.img} alt={p.address} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold text-white" style={{ backgroundColor: sig.color }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
                    {sig.label}
                  </div>
                </div>
                {/* body */}
                <div className="flex-1 min-w-0 p-3 sm:p-4 text-left flex flex-col justify-between">
                  <div>
                    <div className="flex items-baseline gap-2 text-[10px] font-mono uppercase tracking-widest opacity-60">
                      <MapPin size={10} /> {p.city}
                    </div>
                    <h3 className="text-base sm:text-lg font-black tracking-tight leading-tight mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.address}</h3>
                    <p className="text-xs opacity-60 mt-0.5">{p.type}</p>
                  </div>
                  <div className="flex items-end justify-between mt-2 flex-wrap gap-1">
                    <div>
                      <div className="text-lg sm:text-xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: INK }}>£{(p.price / 1000).toFixed(0)}k</div>
                      {topOffer ? (
                        <div className="text-[10px] font-mono font-bold" style={{ color: EMERALD }}>top offer £{(topOffer.offer / 1000).toFixed(0)}k</div>
                      ) : (
                        <div className="text-[10px] font-mono opacity-60">{p.viewings.length} viewing{p.viewings.length === 1 ? "" : "s"} · {p.listedDays}d listed</div>
                      )}
                    </div>
                    <CaretRight size={16} weight="bold" className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" style={{ color: NAVY }} />
                  </div>
                </div>
              </div>
              {/* Signal strip */}
              <div className="px-3 sm:px-4 py-2 border-t text-[11px] italic flex items-center gap-2" style={{ borderColor: RULE, color: sig.color, fontFamily: "'Outfit', serif" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sig.color }} />
                {sig.note}
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-center text-[11px] opacity-60 italic" style={{ fontFamily: "'Outfit', serif" }}>Tap any listing to see viewings, offers and draft the vendor update.</p>
    </section>
  );
}

function DetailView({ property, onLogOpen }) {
  const sig = getSignal(property);
  const vs = property.viewings;
  const yes = vs.filter((v) => v.outcome === "yes");
  const topOffer = yes.map((v) => v.offer).filter(Boolean).sort((a, b) => b - a)[0];
  const pct = topOffer ? Math.round((topOffer / property.price) * 100) : null;

  return (
    <section className="max-w-3xl mx-auto pb-24">
      {/* Hero */}
      <div className="relative aspect-[16/9] sm:aspect-[16/8] overflow-hidden bg-slate-200">
        <img src={property.img} alt={property.address} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(11,18,32,0.85) 100%)" }} />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold text-white" style={{ backgroundColor: sig.color }}>
          <span className="w-1.5 h-1.5 rounded-full bg-white/90" /> {sig.label}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-1 flex items-center gap-1"><MapPin size={10} /> {property.city}</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{property.address}</h1>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>£{property.price.toLocaleString()}</span>
            <span className="text-xs opacity-80">{property.type}</span>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2" data-testid="openhouse-detail-stats">
          <StatMini icon={Eye} label="Viewings" value={vs.length} accent={NAVY} />
          <StatMini icon={Clock} label="Listed" value={`${property.listedDays}d`} accent={INK_SOFT} />
          <StatMini icon={TrendUp} label="Offers" value={yes.length} accent={yes.length > 0 ? EMERALD : INK_SOFT} />
        </div>

        {/* Signal / vendor summary card — the email draft */}
        <div className="bg-white border-2 p-5" style={{ borderColor: sig.color }} data-testid="openhouse-vendor-summary">
          <div className="flex items-center gap-2 mb-2">
            <PaperPlaneTilt size={14} weight="fill" style={{ color: sig.color }} />
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: sig.color }}>Vendor update — draft</p>
          </div>
          <p className="text-sm italic opacity-50 mb-2" style={{ fontFamily: "'Outfit', serif" }}>To: vendor · Subject: Your weekly update on {property.address}</p>
          <p className="leading-relaxed text-sm" style={{ fontFamily: "'Outfit', serif" }}>
            <b>{vs.length}</b> viewing{vs.length === 1 ? "" : "s"} this week.
            {yes.length > 0 && <> <b>{yes.length}</b> offer{yes.length === 1 ? "" : "s"}{topOffer ? <> — top at <b style={{ color: sig.color }}>£{topOffer.toLocaleString()}</b> ({pct}% of asking)</> : ""}.</>}
            {vs.filter((v) => v.outcome === "maybe").length > 0 && <> <b>{vs.filter((v) => v.outcome === "maybe").length}</b> maybe{vs.filter((v) => v.outcome === "maybe").length === 1 ? "" : "s"}.</>}
            {vs.filter((v) => v.outcome === "price").length > 0 && <> <b>{vs.filter((v) => v.outcome === "price").length}</b> price-sensitive.</>}
            {vs.filter((v) => v.outcome === "no").length > 0 && <> <b>{vs.filter((v) => v.outcome === "no").length}</b> firm no{vs.filter((v) => v.outcome === "no").length === 1 ? "" : "s"}.</>}
          </p>
          {sig.note && (
            <div className="mt-3 p-3 flex items-start gap-2" style={{ backgroundColor: PAGE, borderLeft: `3px solid ${sig.color}` }}>
              <ChartLine size={14} weight="bold" style={{ color: sig.color }} className="mt-0.5" />
              <p className="text-sm"><b>Signal:</b> {sig.note}</p>
            </div>
          )}
          <button onClick={() => toast.success("Vendor update sent · they'll see it by Friday morning")} className="mt-4 w-full py-2.5 text-sm font-semibold text-white flex items-center justify-center gap-2" style={{ backgroundColor: sig.color }} data-testid="openhouse-send-update">
            <PaperPlaneTilt size={14} weight="fill" /> Send to vendor
          </button>
        </div>

        {/* Log a viewing CTA */}
        <button onClick={onLogOpen} className="w-full py-4 font-bold text-white flex items-center justify-center gap-2" style={{ backgroundColor: NAVY }} data-testid="openhouse-log-btn">
          <Plus size={18} weight="bold" /> Log a viewing
        </button>

        {/* Viewings log */}
        <div data-testid="openhouse-viewings">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold">Viewings log</p>
            <span className="text-[10px] font-mono opacity-60">{vs.length} total</span>
          </div>
          {vs.length === 0 ? (
            <div className="bg-white border-2 border-dashed p-8 text-center" style={{ borderColor: RULE }}>
              <Eye size={28} weight="duotone" className="mx-auto mb-2 opacity-30" />
              <p className="text-sm opacity-60">No viewings yet · just listed {property.listedDays}d ago</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {vs.map((v) => {
                const o = OUTCOMES.find((x) => x.key === v.outcome) || OUTCOMES[1];
                const Icon = o.icon;
                return (
                  <li key={v.id} className="bg-white border p-4 flex items-start gap-3" style={{ borderColor: RULE }} data-testid={`openhouse-viewing-${v.id}`}>
                    <Icon size={22} weight="fill" style={{ color: o.color }} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div><b style={{ fontFamily: "'Outfit', sans-serif" }}>{v.buyer}</b> <span className="text-xs font-mono opacity-60">· {v.when}</span></div>
                        <div className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: o.color }}>{o.label}{v.offer ? ` · £${(v.offer / 1000).toFixed(0)}k` : ""}</div>
                      </div>
                      {v.note && <p className="text-sm opacity-80 mt-1 italic" style={{ fontFamily: "'Outfit', serif" }}>"{v.note}"</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function StatMini({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border p-3" style={{ borderColor: RULE }}>
      <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest opacity-60">
        <Icon size={10} weight="fill" /> {label}
      </div>
      <div className="text-2xl font-black tracking-tight mt-0.5 leading-none" style={{ color: accent, fontFamily: "'Outfit', sans-serif" }}>{value}</div>
    </div>
  );
}

function LogModal({ property, form, setForm, onCancel, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onCancel}>
      <div className="bg-white max-w-md w-full max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="openhouse-log-modal">
        <div className="px-5 py-4 border-b flex items-start justify-between gap-3" style={{ borderColor: RULE }}>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-0.5">Log viewing</p>
            <h3 className="font-black text-lg leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{property.address}</h3>
          </div>
          <button onClick={onCancel} className="text-2xl opacity-50 leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Buyer name">
            <input value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} placeholder="e.g. Mr & Mrs Patel" className="input-oh" data-testid="openhouse-buyer" />
          </Field>
          <Field label="When">
            <input value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} placeholder="e.g. Sat 15:00" className="input-oh" data-testid="openhouse-when" />
          </Field>
          <Field label="Outcome">
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => {
                const Icon = o.icon;
                return (
                  <button key={o.key} onClick={() => setForm({ ...form, outcome: o.key })} className="p-3 text-sm font-bold border-2 flex items-center gap-2 text-left" style={{ borderColor: form.outcome === o.key ? o.color : RULE, color: form.outcome === o.key ? o.color : INK, backgroundColor: form.outcome === o.key ? o.color + "14" : "white" }} data-testid={`openhouse-outcome-${o.key}`}>
                    <Icon size={14} weight="fill" /> {o.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Offer (£ — optional)">
            <input value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })} type="number" placeholder="e.g. 470000" className="input-oh" data-testid="openhouse-offer" />
          </Field>
          <Field label="Note">
            <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What did they say about it?" className="input-oh" data-testid="openhouse-note" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: RULE }}>
          <button onClick={onCancel} className="px-4 py-2.5 text-sm font-semibold border-2" style={{ borderColor: RULE }}>Cancel</button>
          <button onClick={onSave} className="px-5 py-2.5 text-sm font-bold text-white" style={{ backgroundColor: NAVY }} data-testid="openhouse-save">Save viewing</button>
        </div>
      </div>

      <style>{`
        .input-oh {
          width: 100%;
          border: 2px solid ${RULE};
          padding: 0.75rem 0.9rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          background: white;
        }
        .input-oh:focus { outline: none; border-color: ${NAVY}; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="block mb-1.5 text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</span>
      {children}
    </label>
  );
}
